import React, { useState } from 'react';
import { MessageSquare, Send, Loader2, X, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { BACKEND_URL } from '../config/api';

const CATEGORIES = [
  { id: 'general', label: 'General Inquiry' },
  { id: 'billing', label: 'Billing & Payments' },
  { id: 'technical', label: 'Technical Issue' },
  { id: 'feedback', label: 'Feedback & Suggestions' },
  { id: 'report', label: 'Report a Problem' }
];

export default function ContactSupport({ trigger, isLoggedIn = true }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/support/send`,
        formData,
        { withCredentials: true }
      );
      
      setSuccess(true);
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
        setFormData({ subject: '', category: 'general', message: '' });
      }, 2000);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to contact support');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send message');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (!isLoggedIn) {
      toast.error('Please log in to contact support');
      return;
    }
    setOpen(true);
  };

  return (
    <>
      {/* Trigger Element */}
      <span onClick={handleOpen} className="cursor-pointer">
        {trigger || (
          <button className="flex items-center gap-2 text-orange-500 hover:text-orange-600">
            <MessageSquare className="h-4 w-4" />
            Contact Support
          </button>
        )}
      </span>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-orange-500" />
              Contact Support
            </DialogTitle>
          </DialogHeader>

          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Message Sent!</h3>
              <p className="text-slate-600">We'll get back to you as soon as possible.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-support-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full"
                  data-testid="category-select"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your inquiry"
                  maxLength={200}
                  required
                  className="w-full"
                  data-testid="subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Please describe your issue or question in detail..."
                  rows={5}
                  maxLength={5000}
                  required
                  className="w-full"
                  data-testid="message-input"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {formData.message.length}/5000
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn-secondary flex-1"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="submit-support-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Our support team typically responds within 24 hours.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
